import "./NewsDetail.scss";
import { Article_Plain, Article_Array } from "../../../types/api/article.ts";
import { BlocksRenderer } from "@strapi/blocks-react-renderer";
import { useQuery } from "@tanstack/react-query";
import { getBlobAssetUrl, getStrapiResponse } from "../../../utils/utils.tsx";
import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";

// Function to fetch latest news by category
const fetchLatestNews = async (
  categoryId: number | undefined,
): Promise<Article_Array> => {
  let query =
    "/api/articles?customPopulate=nested&sort[0]=date:desc&pagination[limit]=3";

  if (categoryId) {
    query += `&filters[news_category][id][$eq]=${categoryId}`;
  }

  const response = await getStrapiResponse(query);
  if (!response.ok) {
    throw new Error("Network response was not ok");
  }
  return response.json();
};

const NewsDetail = ({ page }: { page: Article_Plain }) => {
  const { t } = useTranslation();
  const [isCopied, setIsCopied] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    console.log("Current article:", page);
    console.log("Category:", page.news_category);
  }, [page]);

  // Effect to handle external links after content is rendered
  useEffect(() => {
    if (!contentRef.current) return;

    const processExternalLinks = () => {
      const addBlankLinksElements =
        contentRef.current!.querySelectorAll(".addBlankLinks");

      addBlankLinksElements.forEach((element) => {
        const links = element.querySelectorAll("a[href]:not([data-processed])");

        links.forEach((link) => {
          const href = link.getAttribute("href");

          if (href) {
            // Check if link is external (starts with http/https or //)
            const isExternal =
              href.startsWith("http://") ||
              href.startsWith("https://") ||
              href.startsWith("//");

            if (isExternal) {
              link.setAttribute("target", "_blank");
              link.setAttribute("rel", "noopener noreferrer");
            }

            // Mark as processed to avoid duplicate processing
            link.setAttribute("data-processed", "true");
          }
        });
      });

      const videoLinks = contentRef.current!.querySelectorAll(
        'a[href$=".mp4"]:not([data-video-processed])',
      );

      videoLinks.forEach((link) => {
        const anchor = link as HTMLAnchorElement;
        const href = link.getAttribute("href");
        if (!href) return;
        // Create wrapper div
        const wrapper = document.createElement("div");
        wrapper.className = "inner-video-cont";
        // Create video
        const video = document.createElement("video");
        video.controls = true;
        video.loop = true;
        video.playsInline = true;
        video.style.display = "block";
        const source = document.createElement("source");
        source.src = href;
        source.type = "video/mp4";
        video.appendChild(source);
        // Create play button div
        const playDiv = document.createElement("div");
        playDiv.className = "inner-video-cont__play";
        // Append video and playDiv to wrapper
        wrapper.appendChild(video);
        wrapper.appendChild(playDiv);
        // Add click event to playDiv
        playDiv.addEventListener("click", () => {
          video.play();
          playDiv.style.display = "none";
        });
        // Insert wrapper before anchor
        if (anchor.parentNode) {
          anchor.parentNode.insertBefore(wrapper, anchor);
          anchor.style.display = "none";
        }
        anchor.setAttribute("data-video-processed", "true");
      });
    };

    // Process immediately in case content is already rendered
    const timer = setTimeout(processExternalLinks, 0);

    // Set up MutationObserver to watch for content changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
          // Check if any added nodes contain links
          const hasLinks = Array.from(mutation.addedNodes).some(
            (node) =>
              node.nodeType === Node.ELEMENT_NODE &&
              (node as Element).querySelector("a[href]"),
          );

          if (hasLinks) {
            processExternalLinks();
          }
        }
      });
    });

    observer.observe(contentRef.current, {
      childList: true,
      subtree: true,
    });

    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [page.content, page.content2]); // Re-run when content changes

  const {
    data: latestNews,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["latestNews", page.news_category?.id],
    queryFn: () => fetchLatestNews(page.news_category?.id),
  });

  const shareUrl = "https://d.trading/" + "news/" + page.slug;

  const handleCopyClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    navigator.clipboard
      .writeText(shareUrl)
      .then(() => {
        setIsCopied(true);
      })
      .catch((err) => console.error("Copy Error: ", err));
  };

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isCopied) {
      timer = setTimeout(() => {
        setIsCopied(false);
      }, 5000);
    }
    return () => clearTimeout(timer);
  }, [isCopied]);

  const handleCloseClick = () => {
    setIsCopied(false);
  };

  return (
    <section className="news-detail">
      <div className="wrapper">
        <div className="news-detail__left">
          <div className="breadcrumbs">
            <ul>
              <li>
                <a href="/news">{t("News and Perspectives")}</a>
              </li>
              <li>
                <a>{page.name}</a>
              </li>
            </ul>
          </div>
          <h1>{page.name}</h1>
          <div className="news-detail__author">
            {page.authorProfile?.slug ? (
              <span className="news-detail__author--hidden">
                {t("Author")}:{" "}
                <a
                  href={`${page.locale && page.locale !== "en" ? `/${page.locale}` : ""}/team/${page.authorProfile.slug}`}
                  className="news-detail__author-link"
                >
                  {page.authorProfile.name}
                </a>
              </span>
            ) : null}
            {page.author ? (
              <span>
                {t("Author")}: {page.author}
              </span>
            ) : null}
            {page.reviewerProfile?.slug && (
              <span>
                {t("Reviewed by")}:{" "}
                <a
                  href={`${page.locale && page.locale !== "en" ? `/${page.locale}` : ""}/team/${page.reviewerProfile.slug}`}
                  className="news-detail__author-link"
                >
                  {page.reviewerProfile.name}
                </a>
              </span>
            )}
            <span className="news-detail__date">{page.date}</span>
          </div>
          <img
            src={page.image?.url}
            width={"848"}
            title={page.name}
            alt={page.name}
          />
          <div className={"content-new"} ref={contentRef}>
            {page.quotePre ? (
              <div className="quote">
                <div className="text">{page.quotePre.quote}</div>
                <div className="author">{page.quotePre.author}</div>
                <div className="authorTitle">{page.quotePre.position}</div>
              </div>
            ) : (
              ""
            )}
            <div className="text addBlankLinks">
              {page.content ? <BlocksRenderer content={page.content} /> : ""}
            </div>
            {page.quote ? (
              <div className="quote">
                <div className="text">{page.quote.quote}</div>
                <div className="author">{page.quote.author}</div>
                <div className="authorTitle">{page.quote.position}</div>
              </div>
            ) : (
              ""
            )}
            <div className="text addBlankLinks">
              {page.content2 ? <BlocksRenderer content={page.content2} /> : ""}
            </div>
            {page.quoteAfter ? (
              <div className="quote">
                <div className="text">{page.quoteAfter.quote}</div>
                <div className="author">{page.quoteAfter.author}</div>
                <div className="authorTitle">{page.quoteAfter.position}</div>
              </div>
            ) : (
              ""
            )}
            {page.contentCkeditor ? (
              <div
                className="text addBlankLinks ck-content"
                dangerouslySetInnerHTML={{ __html: page.contentCkeditor }}
              />
            ) : (
              ""
            )}
          </div>
        </div>
        <div className="news-detail__right">
          <div className="latest-news">
            <div className="latest-news__top">
              <div className="latest-news__title">
                Latest news{" "}
                {page.news_category ? `in ${page.news_category.name}` : ""}
              </div>
              <div className="latest-news-list">
                {isLoading && (
                  <div className="latest-news__loading">
                    Loading latest news...
                  </div>
                )}
                {isError && (
                  <div className="latest-news__failed">
                    Failed to load latest news
                  </div>
                )}
                {latestNews && latestNews.data && latestNews.data.length > 0 ? (
                  latestNews.data
                    .filter((newsItem) => newsItem.id !== page.id) // Filter out current article
                    .slice(0, 3) // Limit to 3 items
                    .map((newsItem) => (
                      <div className="latest-news-list__item" key={newsItem.id}>
                        <a
                          className="latest-news-list__img"
                          href={`/news/${newsItem.slug}`}
                        >
                          {newsItem.image?.url ? (
                            <img
                              src={newsItem.image.url}
                              width="95"
                              alt={newsItem.name}
                            />
                          ) : (
                            <img
                              src="/img/img-connect.jpg"
                              width="95"
                              alt="Default news image"
                            />
                          )}
                        </a>
                        <div className="latest-news-list__inf">
                          <a
                            className="latest-news-list__name"
                            href={`/news/${newsItem.slug}`}
                          >
                            {newsItem.name}
                          </a>
                          <a
                            className="latest-news-list__plus"
                            href={`/news/${newsItem.slug}`}
                          ></a>
                        </div>
                      </div>
                    ))
                ) : (
                  <div className="latest-news__empty">
                    {t("No other articles in this category")}
                  </div>
                )}
              </div>
            </div>
            <div className="latest-news__bottom">
              <div className="latest-news__bottom-title">{t("Share")}</div>
              <div className="social-list">
                <ul>
                  <li>
                    <a
                      href=""
                      target="_blank"
                      rel="noopener noreferrer"
                      data-link={shareUrl}
                      onClick={handleCopyClick} // Добавляем обработчик клика
                    >
                      <img width="50" alt="copy link" src="/img/copy.svg" />
                    </a>
                  </li>
                  <li>
                    <a
                      href={
                        "https://www.linkedin.com/sharing/share-offsite/?url=" +
                        shareUrl
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img
                        width="50"
                        alt="in"
                        src={getBlobAssetUrl("soc_1_4c09f64443.svg")}
                      />
                    </a>
                  </li>
                  <li>
                    <a
                      href={
                        "https://www.facebook.com/sharer/sharer.php?u=" +
                        shareUrl +
                        "&t=" +
                        page.name
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img
                        width="50"
                        alt="fb"
                        src={getBlobAssetUrl("soc_3_2456fa9571.svg")}
                      />
                    </a>
                  </li>
                </ul>
                <div
                  className={`social-list__copied ${isCopied ? "opened" : ""}`}
                >
                  Link copied
                  <div
                    className="social-list__copied-close"
                    onClick={handleCloseClick}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewsDetail;
